import { useEffect, useState } from 'react'
import {
  Users, KeyRound, Boxes, Plug, ShieldCheck, Palette, Cog, Archive, Building2, BellRing, Sparkles, Radar, PhoneIncoming, Ticket,
} from 'lucide-react'
import { Shell, useShellMeta } from './Shell.js'
import { apiGet } from './primitives.js'
import { navigate } from '../router.js'
import { estEtatSupervision, fmtHeure, type EtatSupervision } from '../supervision/api.js'

/**
 * /settings — la landing Paramètres. Des cartes compactes premium dont les
 * chiffres sont RÉELS (GET /api/v1/admin/summary) — quand une donnée
 * n'existe pas, on l'écrit honnêtement au lieu de l'inventer.
 */

interface Summary {
  users: { total: number; active: number; mfa_enabled: number; sessions_active: number }
  roles: { total: number; custom: number }
  services: { total: number; enabled: number; fresh: number; permissions_catalog: number }
  alerts: { active: number; unacked: number }
  audit: { total: number; last_event_at: string | null }
  integrations: { configured: number; planned: number; planned_names: string[] }
  system: {
    version: string; migrations: number
    worker_heartbeat_at: string | null; worker_alive: boolean
    auth_enforcement: boolean; global_write: boolean; maintenance: boolean
  }
}

export function SettingsHome() {
  const meta = useShellMeta()
  const [s, setS] = useState<Summary | null>(null)
  useEffect(() => {
    apiGet<Summary>('/api/v1/admin/summary').then(setS).catch(() => undefined)
  }, [])
  // L'état de Sonde : apiGet résout aussi sur une erreur — la forme est vérifiée.
  const [sup, setSup] = useState<EtatSupervision | null>(null)
  useEffect(() => {
    apiGet<unknown>('/api/v1/supervision/etat').then((j) => { if (estEtatSupervision(j)) setSup(j) }).catch(() => undefined)
  }, [])
  const metaSupervision = !sup ? 'Sonde — état, ce qu’elle publie, qui surveille'
    : sup.state === 'non_configuree' ? 'Sonde n’est pas encore raccordée'
      : sup.state === 'indisponible' ? `Supervision indisponible${sup.since ? ` depuis ${fmtHeure(sup.since)}` : ''}`
        : `Sonde répond · ${sup.shadow_summary_fr ?? (sup.shadow_mode ? 'mode ombre' : 'publie ses alertes')}`

  const cartes = [
    {
      href: '/settings/users', titre: 'Utilisateurs', Icone: Users, tint: 'violet',
      meta: s ? `${s.users.total} compte${s.users.total > 1 ? 's' : ''} · ${s.users.mfa_enabled} MFA actif${s.users.mfa_enabled > 1 ? 's' : ''} · ${s.users.sessions_active} session${s.users.sessions_active > 1 ? 's' : ''}` : '…',
    },
    {
      href: '/settings/roles', titre: 'Modèles de droits', Icone: KeyRound, tint: 'violet',
      meta: s ? `${s.roles.total} rôles · ${s.roles.custom} personnalisé${s.roles.custom > 1 ? 's' : ''}` : '…',
    },
    {
      href: '/settings/services', titre: 'Services', Icone: Boxes, tint: 'cyan',
      meta: s ? `${s.services.enabled} connecté${s.services.enabled > 1 ? 's' : ''} · ${s.services.fresh === s.services.enabled ? 'tous à jour' : `${s.services.fresh} à jour`} · ${s.services.permissions_catalog} permissions cataloguées` : '…',
    },
    {
      href: '/settings/communications', titre: 'Communications', Icone: BellRing, tint: 'amber',
      meta: 'Alertes → qui, comment, avec quel message · e-mail, In-App, Push (préparé)',
    },
    {
      href: '/settings/integrations', titre: 'Intégrations', Icone: Plug, tint: 'cyan',
      meta: s ? `${s.integrations.configured} configurée${s.integrations.configured > 1 ? 's' : ''} · ${s.integrations.planned} prévue${s.integrations.planned > 1 ? 's' : ''}${s.integrations.planned_names.length ? ` — ${s.integrations.planned_names.join(' · ')}` : ''}` : '…',
    },
    { href: '/settings/supervision', titre: 'Supervision', Icone: Radar, tint: 'cyan', meta: metaSupervision },
    { href: '/settings/interphone', titre: 'Interphone IA', Icone: PhoneIncoming, tint: 'emerald', meta: 'Sacha — qui faire sonner, téléphones de secours, phrases d’accueil, mode test, budget de l’IA' },
    { href: '/settings/soirees', titre: 'Soirées', Icone: Sparkles, tint: 'violet', meta: 'Saisons, familles, salles & DJs, veille, Instagram, recherche web, prévisions, billetteries' },
    // Billetterie Live Club (22/10/2026) : les soirées de la billetterie maison, préparées depuis Gestion.
    { href: '/settings/billetterie', titre: 'Billetterie', Icone: Ticket, tint: 'cyan', meta: 'Billetterie Live Club — gel des modifications, modèles de tarifs, TVA par défaut, tolérance à la porte' },
    {
      href: '/settings/security', titre: 'Sécurité', Icone: ShieldCheck, tint: 'emerald',
      meta: s ? `Mode sûr actif · authentification ${s.system.auth_enforcement ? 'imposée' : 'non imposée'}` : '…',
    },
    {
      href: '/settings/appearance', titre: 'Apparence', Icone: Palette, tint: 'neutral',
      meta: 'Mes préférences — thème, accent, ambiance, sidebar…',
    },
    {
      href: '/settings/general', titre: 'Général', Icone: Building2, tint: 'neutral',
      meta: 'Identité, branding et défauts de l’entreprise',
    },
    {
      href: '/settings/system', titre: 'Système', Icone: Cog, tint: 'neutral',
      meta: s ? `v${s.system.version} · worker ${s.system.worker_alive ? 'actif' : 'SILENCIEUX'} · ${s.system.migrations} migrations` : '…',
    },
    {
      href: '/settings/backups', titre: 'Sauvegardes & restauration', Icone: Archive, tint: 'neutral',
      meta: 'Sauvegarde automatique sur le second disque · test de restauration chaque semaine · archives',
    },
  ] as const

  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger">
        <h1 className="lc-title">Paramètres</h1>
        <p className="lc-subtitle">Administration de Gestion — comptes, rôles, services et configuration.</p>

        <div className="ad-settings-grid">
          {cartes.map((c) => (
            <button
              key={c.href}
              className={`lc-card ad-set-card ${c.tint !== 'neutral' ? `lc-card--${c.tint}` : 'gd-card--muted'}`}
              onClick={() => navigate(c.href)}
            >
              <div className="ad-set-card__head">
                <span className={`lc-card__icon ${c.tint === 'neutral' ? 'gd-icon--muted' : ''}`} style={{ width: 46, height: 46, borderRadius: 13 }} aria-hidden="true">
                  <c.Icone size={22} />
                </span>
                <span className="ad-set-card__title">{c.titre}</span>
              </div>
              <span className="ad-set-card__meta">{c.meta}</span>
            </button>
          ))}
        </div>
      </main>
    </Shell>
  )
}
